import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Box,
  Checkbox,
  IconButton,
  Collapse,
  Flex,
  Tooltip,
  Badge,
  Spinner,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
} from "@chakra-ui/react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { MdEvStation, MdPower, MdLocationOn } from "react-icons/md";
import { motion } from "framer-motion";
import useAxios from "axios-hooks";
import { useSetRecoilState } from "recoil";
import { toastState } from "atoms/toastState";
import logger from "infra/logger";

// Styled components for glass morphism effect
const GlassMorphismCard = motion(Box);

const AccessManagementModal = ({ isOpen, onClose, data, onOpen }) => {
  const [isAllowed, setIsAllowed] = useState([]);
  const [isBlocked, setIsBlocked] = useState([]);
  const setToast = useSetRecoilState(toastState);
  const [stationAccessData, setStationAccessData] = useState([]);
  const [expandedStations, setExpandedStations] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; // Number of chargepoints to show per page

  // Theme colors
  const themeColor = "#23A979";
  const themeColorRgba = "rgba(35, 169, 121, 0.15)";
  const themeColorHover = "rgba(35, 169, 121, 0.08)";
  const glassBg = useColorModeValue(
    "rgba(255, 255, 255, 0.6)",
    "rgba(26, 32, 44, 0.6)"
  );
  const borderColor = useColorModeValue(
    "rgba(35, 169, 121, 0.15)",
    "rgba(35, 169, 121, 0.3)"
  );

  const [{ data: accessData, isLoading: accessLoading }, executeAccess] =
    useAxios(
      {
        method: "GET",
        url: `/api/userStationAccess/propertyOwner/${data?.Id}`,
      },
      { manual: true }
    );

  const [{ data: updateData, loading, error }, update] = useAxios(
    {
      method: "POST",
      url: `/api/userStationAccess/create`,
    },
    { manual: true }
  );
  const chargingStations = [];

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const res = await update({
        data: {
          userId: data?.Id,
          allowedList: isAllowed,
          blockedList: isBlocked,
        },
      });
      setToast({
        isOpen: true,
        title: "Access updated successfully",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      setToast({
        isOpen: true,
        title: "Error updating access",
        description: error?.response?.data?.message || "Please try again",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
      onClose();
    }
  };

  const fetchUserStationAccessData = async () => {
    try {
      setIsLoading(true);
      setIsAllowed([]);
      setIsBlocked([]);

      const res = await executeAccess();
      const array = res?.data?.data;
      setStationAccessData(res?.data?.data);

      array?.forEach((item) => {
        const stations = item?.name || [];
        if (stations.length > 0) {
          chargingStations.push(stations);
        }

        const cps = item?.chargePoints || [];
        if (cps.length > 0) {
          cps.forEach((cp) => {
            if (cp?.isAllowed) {
              setIsAllowed((prev) => {
                if (!prev.includes(cp?.id)) {
                  return [...prev, cp?.id];
                }
                return prev;
              });
            } else {
              setIsBlocked((prev) => {
                if (!prev.includes(cp?.id)) {
                  return [...prev, cp?.id];
                }
                return prev;
              });
            }
          });
        }
      });

      // Initialize all stations as expanded
      const initialExpandedState = {};
      array?.forEach((_, index) => {
        initialExpandedState[index] = true;
      });
      setExpandedStations(initialExpandedState);
    } catch (error) {
      logger.log(error);
      setToast({
        isOpen: true,
        title: "Error fetching access data",
        description: "Please try again",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpand = (index) => {
    setExpandedStations((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Filter out stations without chargepoints and apply search filter
  const filteredStationData = stationAccessData
    ?.filter((station) => {
      // First check if station has chargepoints
      if (!station.chargePoints || station.chargePoints.length === 0) {
        return false;
      }

      // Apply search filter
      return (
        searchQuery === "" ||
        station.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (station.address &&
          station.address !== "none" &&
          station.address !== "Orphan" &&
          station.address.toLowerCase().includes(searchQuery.toLowerCase())) ||
        station.chargePoints.some(
          (cp) =>
            cp.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
            // Search by power - convert power to string for comparison
            (cp.power && cp.power.toString().includes(searchQuery))
        )
      );
    });

  const stations = filteredStationData?.map((station, index) => {
    const chargePoints = station?.chargePoints.map((chargepoint, cpIndex) => {
      const handleCheckboxChange = (e) => {
        const isChecked = e.target.checked;
        if (isChecked) {
          if (!isAllowed.includes(chargepoint.id)) {
            setIsBlocked((prev) => prev.filter((id) => id !== chargepoint.id));
            setIsAllowed((prev) => [...prev, chargepoint.id]);
          }
        } else {
          if (!isBlocked.includes(chargepoint.id)) {
            setIsAllowed((prev) => prev.filter((id) => id !== chargepoint.id));
            setIsBlocked((prev) => [...prev, chargepoint.id]);
          }
        }
      };

      const isChargePointAllowed = isAllowed.includes(chargepoint.id);

      return (
        <Box
          key={cpIndex}
          as={motion.div}
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
          bg={isChargePointAllowed ? themeColorRgba : "transparent"}
          borderRadius="md"
          p={1.5}
          m={0.5}
          minW="160px"
          maxW="180px"
          border="1px solid"
          borderColor={isChargePointAllowed ? themeColor : borderColor}
          boxShadow={
            isChargePointAllowed ? `0 0 8px rgba(35, 169, 121, 0.3)` : "none"
          }
          _hover={{
            borderColor: themeColor,
            boxShadow: "0 0 8px rgba(35, 169, 121, 0.2)",
          }}
          position="relative"
        >
          <Flex justify="space-between" align="center">
            <Flex align="center">
              <Checkbox
                colorScheme="brand"
                isChecked={isChargePointAllowed}
                onChange={handleCheckboxChange}
                mr={2}
                sx={{
                  ".chakra-checkbox__control": {
                    borderColor: themeColor,
                    _checked: {
                      bg: themeColor,
                      borderColor: themeColor,
                    },
                  },
                }}
              />
              <Box
                bg={themeColorRgba}
                p={1}
                borderRadius="md"
                mr={2}
                color={themeColor}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <MdEvStation size={14} />
              </Box>
              <Flex direction="column">
                <Text
                  fontSize="sm"
                  fontWeight={isChargePointAllowed ? "bold" : "normal"}
                  color={isChargePointAllowed ? themeColor : "inherit"}
                >
                  {chargepoint.code}
                </Text>
                <Flex alignItems="center" mt={1} fontSize="xs" color="gray.500">
                  <Text>{chargepoint.typeOfCharger}</Text>
                  <Box mx={1}>•</Box>
                  <MdPower style={{ marginRight: "4px" }} />
                  <Text>{chargepoint.power} kW</Text>
                </Flex>
              </Flex>
            </Flex>
            {isChargePointAllowed && (
              <Tooltip label="Access Granted" placement="top">
                <Box color={themeColor}>
                  <CheckIcon boxSize={4} />
                </Box>
              </Tooltip>
            )}
          </Flex>
        </Box>
      );
    });

    return (
      <GlassMorphismCard
        key={index}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.1 }}
        backdropFilter="blur(12px)"
        bg={glassBg}
        p={2.5}
        borderRadius="xl"
        w="100%"
        mt={3}
        mb={1.5}
        borderWidth="1px"
        borderColor={borderColor}
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.05)"
        overflow="hidden"
        position="relative"
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "2px",
          // background: `linear-gradient(90deg, transparent, ${themeColor}, transparent)`,
          opacity: 0.7,
        }}
      >
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center">
            <Box
              bg={themeColorRgba}
              p={1.5}
              borderRadius="md"
              mr={2}
              color={themeColor}
            >
              <MdLocationOn size={18} />
            </Box>
            <Box>
              <Text fontWeight="bold" fontSize="md">
                {station.name}
              </Text>
              {station.address &&
                station.address !== "none" &&
                station.address !== "Orphan" && (
                  <Text fontSize="xs" color="gray.500" mt={0.5}>
                    {station.address}
                  </Text>
                )}
            </Box>
          </Flex>

          <Flex alignItems="center">
            <Badge
              colorScheme="brand"
              variant="subtle"
              mr={3}
              fontSize="xs"
              bg={themeColorRgba}
              color={themeColor}
            >
              {station.chargePoints?.length || 0} Chargepoint
              {station.chargePoints?.length !== 1 ? "s" : ""}
            </Badge>

            <IconButton
              icon={
                expandedStations[index] ? (
                  <ChevronUpIcon />
                ) : (
                  <ChevronDownIcon />
                )
              }
              onClick={() => toggleExpand(index)}
              variant="ghost"
              color={themeColor}
              _hover={{ bg: themeColorHover }}
              aria-label="Toggle Chargepoints"
              size="sm"
            />
          </Flex>
        </Flex>

        <Collapse in={expandedStations[index]} animateOpacity>
          <Box
            mt={3}
            maxH="200px"
            overflowY="auto"
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(0, 0, 0, 0.05)",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: themeColorRgba,
                borderRadius: "10px",
              },
            }}
          >
            <Flex flexWrap="wrap" justifyContent="flex-start">
              {chargePoints}
            </Flex>
          </Box>
        </Collapse>
      </GlassMorphismCard>
    );
  });
  const closeHandler = () => {
    setIsAllowed([]);
    setIsBlocked([]);
    onClose();
  };

  useEffect(() => {
    if (isOpen && data?.Id) {
      fetchUserStationAccessData();
    }
  }, [isOpen, data]);

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={closeHandler}
      onOpen={fetchUserStationAccessData}
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="rgba(0, 0, 0, 0.4)" backdropFilter="blur(5px)" />
      <ModalContent
        as={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        maxWidth={{ base: "95%", md: "85%", lg: "70%" }}
        minH={"40vh"}
        bg={glassBg}
        backdropFilter="blur(12px)"
        borderRadius="xl"
        boxShadow="0 8px 32px rgba(0, 0, 0, 0.1)"
        borderWidth="1px"
        borderColor={borderColor}
        position="relative"
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          // background: `linear-gradient(90deg, transparent, ${themeColor}, transparent)`,
          opacity: 0.8,
        }}
      >
        <ModalHeader
          textAlign="center"
          fontSize="2xl"
          fontWeight="bold"
          color={themeColor}
          pt={6}
        >
          Access Management
        </ModalHeader>
        <ModalCloseButton
          color={themeColor}
          _hover={{ bg: themeColorHover }}
          size="lg"
          mt={2}
          mr={2}
        />

        <ModalBody>
          {isLoading ? (
            <Flex
              justify="center"
              align="center"
              minH="300px"
              direction="column"
            >
              <Spinner
                size="xl"
                thickness="4px"
                speed="0.65s"
                color={themeColor}
              />
              <Text mt={4} color="gray.500">
                Loading access data...
              </Text>
            </Flex>
          ) : (
            <Box p={3} pt={0}>
              <Flex align="center" justify="space-between" mb={4}>
                <Text
                  fontSize="xl"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                >
                  <MdLocationOn
                    color={themeColor}
                    style={{ marginRight: "8px" }}
                    size={24}
                  />
                  Locations
                </Text>
                <Badge
                  colorScheme="brand"
                  variant="subtle"
                  fontSize="md"
                  py={1}
                  px={3}
                  borderRadius="full"
                  bg={themeColorRgba}
                  color={themeColor}
                >
                  {filteredStationData?.length || 0} Stations
                </Badge>
              </Flex>

              <InputGroup mb={4} size="md">
                <InputLeftElement
                  pointerEvents="none"
                  children={<SearchIcon color={themeColor} />}
                />
                <Input
                  placeholder="Search by location name, address, chargepoint code, or power (kW)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  borderColor={borderColor}
                  _focus={{
                    borderColor: themeColor,
                    boxShadow: `0 0 0 1px ${themeColor}`,
                  }}
                  _hover={{
                    borderColor: `rgba(35, 169, 121, 0.5)`,
                  }}
                  bg="rgba(255, 255, 255, 0.1)"
                  backdropFilter="blur(12px)"
                  as={motion.input}
                  whileFocus={{ scale: 1.01 }}
                  transition={{ duration: 0.2 }}
                />
              </InputGroup>

              {isAllowed.length > 0 && (
                <Box
                  mb={4}
                  p={3}
                  borderRadius="lg"
                  bg="rgba(255, 255, 255, 0.05)"
                  backdropFilter="blur(8px)"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <Flex align="center" mb={2}>
                    <Box
                      bg={themeColorRgba}
                      p={1}
                      borderRadius="md"
                      mr={2}
                      color={themeColor}
                    >
                      <CheckIcon boxSize={3} />
                    </Box>
                    <Text fontWeight="medium" fontSize="sm" color={themeColor}>
                      {searchQuery === ""
                        ? `${isAllowed.length} Selected Chargepoint${
                            isAllowed.length !== 1 ? "s" : ""
                          }`
                        : `Filtered Selected Chargepoints`}
                    </Text>
                  </Flex>

                  <Box overflowX="auto" maxHeight="80px">
                    <Flex direction="column">
                      <Flex flexWrap="wrap" gap={1} justifyContent="flex-start">
                        {stationAccessData
                          ?.flatMap(
                            (station) =>
                              station.chargePoints?.filter(
                                (cp) =>
                                  isAllowed.includes(cp.id) &&
                                  (searchQuery === "" ||
                                    cp.code
                                      .toLowerCase()
                                      .includes(searchQuery.toLowerCase()) ||
                                    (station.name &&
                                      station.name
                                        .toLowerCase()
                                        .includes(searchQuery.toLowerCase())) ||
                                    (station.address &&
                                      station.address !== "none" &&
                                      station.address !== "Orphan" &&
                                      station.address
                                        .toLowerCase()
                                        .includes(searchQuery.toLowerCase())) ||
                                    (cp.power &&
                                      cp.power
                                        .toString()
                                        .includes(searchQuery)))
                              ) || []
                          )
                          // Apply pagination
                          .slice(
                            (currentPage - 1) * itemsPerPage,
                            currentPage * itemsPerPage
                          )
                          .map((cp, index) => (
                            <Box
                              key={index}
                              bg={themeColorRgba}
                              p={1.5}
                              m={0.5}
                              borderRadius="md"
                              minW="110px"
                              maxW="150px"
                              border="1px solid"
                              borderColor={themeColor}
                              as={motion.div}
                              whileHover={{ scale: 1.05 }}
                              transition={{ duration: 0.2 }}
                              boxShadow="0 0 8px rgba(35, 169, 121, 0.2)"
                              height="60px"
                              overflow="hidden"
                            >
                              <Flex align="center">
                                <Box
                                  bg={themeColorRgba}
                                  p={2}
                                  borderRadius="md"
                                  mr={2}
                                  color={themeColor}
                                >
                                  <MdEvStation size={16} />
                                </Box>
                                <Text
                                  fontSize="md"
                                  fontWeight="bold"
                                  color={themeColor}
                                  noOfLines={1}
                                >
                                  {cp.code}
                                </Text>
                              </Flex>
                              <Text
                                fontSize="2xs"
                                mt={0.5}
                                color="gray.500"
                                noOfLines={1}
                              >
                                {cp.typeOfCharger} • {cp.power} kW
                              </Text>
                            </Box>
                          ))}
                      </Flex>

                      {/* Pagination Controls */}
                      {(() => {
                        const filteredChargepoints =
                          stationAccessData?.flatMap(
                            (station) =>
                              station.chargePoints?.filter(
                                (cp) =>
                                  isAllowed.includes(cp.id) &&
                                  (searchQuery === "" ||
                                    cp.code
                                      .toLowerCase()
                                      .includes(searchQuery.toLowerCase()) ||
                                    (station.name &&
                                      station.name
                                        .toLowerCase()
                                        .includes(searchQuery.toLowerCase())) ||
                                    (station.address &&
                                      station.address !== "none" &&
                                      station.address !== "Orphan" &&
                                      station.address
                                        .toLowerCase()
                                        .includes(searchQuery.toLowerCase())) ||
                                    (cp.power &&
                                      cp.power
                                        .toString()
                                        .includes(searchQuery)))
                              ) || []
                          ) || [];

                        const totalPages = Math.ceil(
                          filteredChargepoints.length / itemsPerPage
                        );

                        return totalPages > 1 ? (
                          <Flex justify="center" mt={3} align="center">
                            <IconButton
                              icon={<ChevronLeftIcon />}
                              onClick={() =>
                                setCurrentPage((prev) => Math.max(prev - 1, 1))
                              }
                              isDisabled={currentPage === 1}
                              size="sm"
                              variant="ghost"
                              color={themeColor}
                              _hover={{ bg: themeColorHover }}
                              aria-label="Previous Page"
                              mr={1}
                            />

                            <Text fontSize="sm" color="gray.500">
                              Page {currentPage} of {totalPages}
                            </Text>

                            <IconButton
                              icon={<ChevronRightIcon />}
                              onClick={() =>
                                setCurrentPage((prev) =>
                                  Math.min(prev + 1, totalPages)
                                )
                              }
                              isDisabled={currentPage === totalPages}
                              size="sm"
                              variant="ghost"
                              color={themeColor}
                              _hover={{ bg: themeColorHover }}
                              aria-label="Next Page"
                              ml={1}
                            />

                            <Text fontSize="xs" color="gray.500" ml={2}>
                              {filteredChargepoints.length} chargepoint
                              {filteredChargepoints.length !== 1
                                ? "s"
                                : ""}{" "}
                              total
                            </Text>
                          </Flex>
                        ) : null;
                      })()}
                    </Flex>
                  </Box>
                </Box>
              )}

              <Box
                maxH="60vh"
                overflowY="auto"
                pr={2}
                css={{
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "rgba(0, 0, 0, 0.05)",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: themeColorRgba,
                    borderRadius: "10px",
                  },
                }}
              >
                {filteredStationData?.length > 0 ? (
                  stations
                ) : (
                  <Flex
                    justify="center"
                    align="center"
                    minH="200px"
                    direction="column"
                    bg="rgba(0, 0, 0, 0.02)"
                    borderRadius="xl"
                    p={6}
                  >
                    <MdLocationOn
                      color={themeColor}
                      size={40}
                      style={{ opacity: 0.5 }}
                    />
                    <Text mt={4} color="gray.500">
                      No locations with chargepoints available
                    </Text>
                  </Flex>
                )}
              </Box>
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            as={motion.button}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving"
            bg={themeColor}
            color="white"
            _hover={{ bg: "rgba(35, 169, 121, 0.9)" }}
            _active={{ bg: "rgba(35, 169, 121, 0.8)" }}
            borderRadius="lg"
            px={8}
            py={5}
            fontSize="md"
            fontWeight="bold"
            boxShadow="0 4px 10px rgba(35, 169, 121, 0.3)"
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AccessManagementModal;
